import { Link, useParams } from "react-router-dom";
import { EmptyState, MarkdownContent, Skeleton, stripLeadingH1 } from "@cloudox/ui";
import { PageNotice } from "@/components/PageNotice";
import { useKnowledgeContext } from "@/components/layout/KnowledgeShell";
import { useDocumentSlug } from "@/hooks/useKnowledgeData";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { relatedPagePath } from "@/lib/preview";

/**
 * A cross-cutting intelligence page (Environment Evolution, Cost Intelligence,
 * Knowledge Quality).
 *
 * These were published in the bundle and declared in the navigation manifest,
 * but the app had no route for them, so three shipped layers were unreachable —
 * and the narrated prose cross-references at least one of them by name, leaving
 * a dead pointer in the report.
 *
 * They are rendered as whole documents rather than split into sections: they
 * are standalone analyses, not audience lenses, and the manifest gives them no
 * section structure to navigate by.
 */
export function RelatedPagePage() {
  const { page } = useParams<{ page: string }>();
  const { provider, nav, quality } = useKnowledgeContext();
  const meta = nav.relatedPages.find((p) => p.key === page);
  useDocumentTitle(meta?.title ?? "Page not found");

  if (!meta) {
    return (
      <EmptyState
        headingLevel={1}
        title="Page not found"
        description={`This run published no "${page ?? ""}" page.`}
        action={
          <Link to="/" className="btn btn-secondary">
            Back to the overview
          </Link>
        }
      />
    );
  }

  return (
    <article>
      <header className="mb-5">
        <p
          className="text-[11px] font-semibold uppercase tracking-[0.12em]"
          style={{ color: "var(--fg-subtle)" }}
        >
          Intelligence layer
        </p>
        <h1 className="mt-1 text-2xl font-semibold" style={{ color: "var(--fg)" }}>
          {meta.title}
        </h1>
        {meta.description ? (
          <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
            {meta.description}
          </p>
        ) : null}
      </header>
      {meta.key === QUALITY_PAGE_KEY ? (
        <div className="mb-5">
          <QualityScoreFraming schemaVersion={quality?.schemaVersion} />
        </div>
      ) : null}
      <RelatedBody slug={meta.key} provider={provider} />
      <nav
        className="mt-10 flex flex-wrap gap-2 border-t pt-5"
        style={{ borderColor: "var(--border)" }}
        aria-label="Other intelligence layers"
      >
        {nav.relatedPages
          .filter((p) => p.key !== meta.key)
          .map((p) => (
            <Link key={p.key} to={relatedPagePath(p.key)} className="btn btn-secondary">
              {p.title}
            </Link>
          ))}
      </nav>
    </article>
  );
}

const QUALITY_PAGE_KEY = "knowledge-quality";

/**
 * Context for the Knowledge Quality page's headline figure.
 *
 * Two things about that number are easy to misread and neither is stated on
 * the page itself:
 *
 *  1. It scores **this report**, not the product. It answers "does each view
 *     serve its audience", and is not the separate delivery-readiness decision
 *     that governs whether a report may be sent.
 *  2. It was produced by an earlier version of the quality engine. The engine
 *     has since changed how the run-level headline is derived — most
 *     materially, the run grade is now capped at the weakest view's grade — so
 *     a figure carried over from an older run is not what today's engine would
 *     publish for the same views.
 *
 * We deliberately do **not** recompute or restate a corrected grade here. The
 * score belongs to the run that produced it; a frontend that quietly issues a
 * different number would be inventing a quality verdict nobody measured. The
 * honest move is to say which engine wrote it and what has changed since.
 */
function QualityScoreFraming({ schemaVersion }: { readonly schemaVersion: number | undefined }) {
  return (
    <PageNotice title="How to read these scores">
      <p>
        This measures <strong>the quality of this report</strong> — whether each Knowledge View
        answers its audience&apos;s questions. It is not a statement about the environment, about
        CloudoX&apos;s readiness, or about whether a report is approved for delivery, which is a
        separate decision recorded elsewhere.
      </p>
      <p>
        The figures come from the demo run that generated this example
        {typeof schemaVersion === "number"
          ? `, written by version ${schemaVersion} of the quality engine`
          : null}
        . The engine has since changed how the run-level headline is derived — notably, a run&apos;s
        grade is now capped at its weakest view&apos;s grade, and no overall figure is published
        unless every expected view was scored. Read the per-view scorecards below as the substance;
        treat the single headline number as belonging to that earlier run.
      </p>
      <p>
        Where the page below calls its scores &ldquo;authoritative&rdquo;, that means the
        deterministic engine outranks the optional AI review — not that the figures are current.
      </p>
    </PageNotice>
  );
}

function RelatedBody({
  slug,
  provider,
}: {
  readonly slug: string;
  readonly provider: ReturnType<typeof useKnowledgeContext>["provider"];
}) {
  const doc = useDocumentSlug(provider, slug);

  if (doc.status === "loading") {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-40" />
      </div>
    );
  }
  if (doc.status === "error") {
    return <EmptyState title="Page unavailable" description="This page could not be loaded." />;
  }
  return (
    <div className="doc-prose">
      <MarkdownContent markdown={stripLeadingH1(doc.value.markdown)} />
    </div>
  );
}
